package com.tovtalk.ui.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.share.ShareHelper
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
actual fun ShareBottomSheet(
    deepLinkUrl: String,
    onDismiss: () -> Unit
) {
    // Try Web Share API first if available
    val canUseWebShare = remember {
        js("typeof navigator !== 'undefined' && navigator.share !== undefined") as Boolean
    }

    if (canUseWebShare) {
        LaunchedEffect(deepLinkUrl) {
            try {
                val shareData = js("({})")
                shareData.url = deepLinkUrl
                val nav = window.navigator.asDynamic()
                val promise = nav.share(shareData) as kotlin.js.Promise<Unit>
                kotlin.coroutines.suspendCoroutine<Unit> { cont ->
                    promise.then(
                        { cont.resumeWith(Result.success(Unit)) },
                        { cont.resumeWith(Result.success(Unit)) } // User cancelled or error — dismiss either way
                    )
                }
            } catch (e: Throwable) {
                // Web Share API failed — ignore
            }
            onDismiss()
        }
        return
    }

    // Fallback: copy-only UI (same as iOS stub)
    var copied by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        containerColor = TovTalkTheme.colors.surfaceElevated,
        shape = TovTalkTheme.shapes.sheet
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = TovTalkTheme.spacing.lg)
                .padding(bottom = TovTalkTheme.spacing.xl)
                .navigationBarsPadding()
        ) {
            Text(
                text = com.tovtalk.utils.strings.StringProvider.get("share_link_title"),
                style = TovTalkTheme.typography.titleMedium,
                color = TovTalkTheme.colors.textPrimary
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
            Text(
                text = deepLinkUrl,
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textSecondary
            )
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
            HorizontalDivider(color = TovTalkTheme.colors.border)
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
            TextButton(
                onClick = {
                    scope.launch {
                        val ok = ShareHelper.copyToClipboard(deepLinkUrl)
                        copied = ok
                        if (ok) {
                            delay(1500)
                            copied = false
                        }
                    }
                },
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    text = if (copied) com.tovtalk.utils.strings.StringProvider.get("share_copied") else com.tovtalk.utils.strings.StringProvider.get("share_copy"),
                    style = TovTalkTheme.typography.labelLarge,
                    color = TovTalkTheme.colors.brand
                )
            }
        }
    }
}
